import { supabase } from '@/core/database/supabase';
import type { EstimateItem, EstimateItemInput, EstimateItemStats } from '../types/estimate.types';

function toNumber(value: unknown) {
  return Number(value || 0);
}

function normalizeItem(row: any, source: EstimateItem['source'], estimateIdField: 'estimate_id' | 'boq_id'): EstimateItem {
  return {
    id: row.id,
    company_id: row.company_id,
    project_id: row.project_id,
    estimate_id: row[estimateIdField],
    parent_id: row.parent_id,
    zone_id: row.zone_id,
    phase_id: row.phase_id,
    category_code: row.category_code || 'general',
    item_code: row.item_code || '',
    item_name: row.item_name || '',
    description: row.description,
    specification: row.specification,
    species: row.species,
    unit: row.unit || '',
    quantity: toNumber(row.quantity),
    unit_price: toNumber(row.unit_price),
    material_cost: toNumber(row.material_cost),
    labor_cost: toNumber(row.labor_cost),
    equipment_cost: toNumber(row.equipment_cost),
    subcontract_cost: toNumber(row.subcontract_cost),
    total_amount: toNumber(row.total_amount),
    waste_factor: toNumber(row.waste_factor),
    process_template_id: row.process_template_id || null,
    process_step_id: row.process_step_id || null,
    source_type: row.source_type || null,
    source_id: row.source_id || null,
    status_code: row.status_code || 'draft',
    sort_order: toNumber(row.sort_order),
    created_at: row.created_at,
    updated_at: row.updated_at,
    zone: row.zone || null,
    phase: row.phase || null,
    source,
  };
}

function calcStats(items: EstimateItem[]): EstimateItemStats {
  const totalAmount = items.reduce((sum, item) => sum + item.total_amount, 0);
  return {
    totalAmount,
    itemCount: items.length,
    materialCost: items.reduce((sum, item) => sum + item.material_cost, 0),
    laborCost: items.reduce((sum, item) => sum + item.labor_cost, 0),
    equipmentCost: items.reduce((sum, item) => sum + item.equipment_cost, 0),
    subcontractCost: items.reduce((sum, item) => sum + item.subcontract_cost, 0),
    warningCount: items.filter((item) => item.unit_price <= 0 || item.quantity <= 0).length,
  };
}

export const estimateItemsService = {
  async getByEstimateId(estimateId: string): Promise<{ items: EstimateItem[]; stats: EstimateItemStats }> {
    const estimateItems = await this.fetchEstimateItems(estimateId);
    const items = estimateItems.length > 0 ? estimateItems : await this.fetchBoqItemsFallback(estimateId);
    return { items, stats: calcStats(items) };
  },

  async fetchEstimateItems(estimateId: string): Promise<EstimateItem[]> {
    const { data, error } = await supabase
      .from('estimate_items' as any)
      .select(`
        *,
        zone:project_zones(id, name:zone_name),
        phase:project_phases(id, name)
      `)
      .eq('estimate_id', estimateId)
      .order('sort_order', { ascending: true });

    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('estimate_items');
      if (missingTable) return [];
      throw error;
    }

    return ((data || []) as any[]).map((row) => normalizeItem(row, 'estimate_items', 'estimate_id'));
  },

  async fetchBoqItemsFallback(boqId: string): Promise<EstimateItem[]> {
    const { data, error } = await supabase
      .from('construction_boq_items')
      .select(`
        *,
        zone:project_zones(id, name:zone_name),
        phase:project_phases(id, name)
      `)
      .eq('boq_id', boqId)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return ((data || []) as any[]).map((row) => normalizeItem(row, 'construction_boq_items', 'boq_id'));
  },

  async create(input: EstimateItemInput): Promise<EstimateItem> {
    const totalAmount = input.quantity * input.unit_price;
    const { data, error } = await supabase
      .from('estimate_items' as any)
      .insert({
        ...input,
        material_cost: input.material_cost || totalAmount,
        labor_cost: input.labor_cost || 0,
        equipment_cost: input.equipment_cost || 0,
        subcontract_cost: input.subcontract_cost || 0,
        total_amount: totalAmount,
        waste_factor: input.waste_factor || 0,
        sort_order: input.sort_order || 0,
        status_code: 'draft',
      })
      .select(`
        *,
        zone:project_zones(id, name:zone_name),
        phase:project_phases(id, name)
      `)
      .single();

    if (error) throw error;
    await this.recalculateHeaderTotals(input.estimate_id);
    return normalizeItem(data, 'estimate_items', 'estimate_id');
  },

  async update(id: string, updates: Partial<EstimateItemInput>): Promise<void> {
    const totalAmount =
      updates.quantity !== undefined && updates.unit_price !== undefined
        ? updates.quantity * updates.unit_price
        : undefined;

    const { data: current, error: currentError } = await supabase
      .from('estimate_items' as any)
      .select('estimate_id, quantity, unit_price')
      .eq('id', id)
      .single();

    if (currentError) throw currentError;

    const nextQuantity = updates.quantity ?? Number((current as any).quantity || 0);
    const nextUnitPrice = updates.unit_price ?? Number((current as any).unit_price || 0);
    const { error } = await supabase
      .from('estimate_items' as any)
      .update({
        ...updates,
        total_amount: totalAmount ?? nextQuantity * nextUnitPrice,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;
    await this.recalculateHeaderTotals((current as any).estimate_id);
  },

  async delete(id: string): Promise<void> {
    const { data: current, error: currentError } = await supabase
      .from('estimate_items' as any)
      .select('estimate_id')
      .eq('id', id)
      .single();

    if (currentError) throw currentError;

    const { error } = await supabase.from('estimate_items' as any).delete().eq('id', id);
    if (error) throw error;
    await this.recalculateHeaderTotals((current as any).estimate_id);
  },

  async recalculateHeaderTotals(estimateId: string): Promise<void> {
    const { data, error } = await supabase
      .from('estimate_items' as any)
      .select('material_cost, labor_cost, equipment_cost, subcontract_cost, total_amount')
      .eq('estimate_id', estimateId);

    if (error) throw error;

    const rows = (data || []) as any[];
    const totals = rows.reduce(
      (acc, item) => ({
        material_cost: acc.material_cost + Number(item.material_cost || 0),
        labor_cost: acc.labor_cost + Number(item.labor_cost || 0),
        equipment_cost: acc.equipment_cost + Number(item.equipment_cost || 0),
        subcontract_cost: acc.subcontract_cost + Number(item.subcontract_cost || 0),
        total_amount: acc.total_amount + Number(item.total_amount || 0),
      }),
      { material_cost: 0, labor_cost: 0, equipment_cost: 0, subcontract_cost: 0, total_amount: 0 },
    );

    const { error: updateError } = await supabase
      .from('estimate_headers' as any)
      .update({
        ...totals,
        subtotal_amount: totals.total_amount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', estimateId);

    if (updateError) throw updateError;
  },
};
